import React, { useEffect, useRef, useState } from 'react';
import {
  Building2,
  Plus,
  Trash2,
  Loader2,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react';
import { ImageSourceButtons } from '../common/ImageSourceButtons';
import { fileToCompressedDataUrl, formatBytesShort, MAX_PHOTO_BYTES } from '../../lib/imageResize';
import {
  readCompanyProfile,
  saveCompanyProfile,
  hydrateCompanyProfile,
  cacheCompanyProfile,
  keepVisibleLogo,
  type CompanyProfile,
  type CompanyPartner,
} from '../../lib/companyProfile';
import { uploadsApi, hasAuthToken } from '../../api';
import { mediaSrc } from '../../lib/mediaUrl';
import { useTranslation } from '../../lib/i18n';

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

function newPartner(): CompanyPartner {
  return { id: crypto.randomUUID(), name: '', role: '', phone: '', email: '', share: '' };
}

const CompanySettings: React.FC = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState<CompanyProfile>(() => readCompanyProfile());
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadNote, setUploadNote] = useState<string | null>(null);
  /** Bumped when the user sets a logo so an in-flight profile fetch cannot clear it. */
  const profileEpoch = useRef(0);
  const logoTouched = useRef(false);

  // Pull the freshest copy from the backend on mount.
  useEffect(() => {
    const epoch = profileEpoch.current;
    let active = true;
    hydrateCompanyProfile().then((p) => {
      if (!active || !p || epoch !== profileEpoch.current) return;
      setForm((current) => ({
        ...p,
        logoUrl: logoTouched.current ? current.logoUrl : keepVisibleLogo(current.logoUrl, p.logoUrl),
      }));
    });
    return () => {
      active = false;
    };
  }, []);

  const set = <K extends keyof CompanyProfile>(key: K, value: CompanyProfile[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setSaveState('idle');
  };

  const setPartner = (id: string, patch: Partial<CompanyPartner>) => {
    setForm((f) => ({
      ...f,
      partners: f.partners.map((p) => (p.id === id ? { ...p, ...patch } : p)),
    }));
    setSaveState('idle');
  };

  const addPartner = () => {
    setForm((f) => ({ ...f, partners: [...f.partners, newPartner()] }));
    setSaveState('idle');
  };

  const removePartner = (id: string) => {
    setForm((f) => ({ ...f, partners: f.partners.filter((p) => p.id !== id) }));
    setSaveState('idle');
  };

  const handleLogo = async (files: File[]) => {
    const file = files[0];
    if (!file) return;
    setUploading(true);
    setUploadNote(null);
    let dataUrl: string;
    try {
      dataUrl = await fileToCompressedDataUrl(file);
    } catch {
      setUploading(false);
      setUploadNote('Could not process this image.');
      return;
    }
    if (!dataUrl) {
      setUploading(false);
      setUploadNote('Could not read the selected file.');
      return;
    }
    profileEpoch.current += 1;
    logoTouched.current = true;
    // Show and store the compressed image itself. The shop_<id>.jpg link is not
    // on Cloudinary, so swapping to it makes the logo vanish after a second.
    set('logoUrl', dataUrl);
    cacheCompanyProfile({ ...readCompanyProfile(), logoUrl: dataUrl });
    if (hasAuthToken()) {
      try {
        await uploadsApi.image(dataUrl, 'company');
        setUploadNote(`Logo uploaded (${formatBytesShort(MAX_PHOTO_BYTES)} max before upload).`);
      } catch {
        setUploadNote(`Saved locally — compressed to under ${formatBytesShort(MAX_PHOTO_BYTES)}.`);
      }
    } else {
      setUploadNote(`Saved locally — compressed to under ${formatBytesShort(MAX_PHOTO_BYTES)}.`);
    }
    setUploading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveState('saving');
    setError(null);
    try {
      const saved = await saveCompanyProfile(form);
      setForm(saved);
      setSaveState('saved');
      window.setTimeout(() => setSaveState((s) => (s === 'saved' ? 'idle' : s)), 2500);
    } catch (err) {
      setSaveState('error');
      setError(err instanceof Error ? err.message : 'Could not save company details.');
    }
  };

  const reset = () => {
    setForm(readCompanyProfile());
    setSaveState('idle');
    setError(null);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <div className="flex items-start gap-2 text-zinc-900">
        <Building2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" aria-hidden />
        <p className="text-sm text-zinc-600">
          {t('settings.companyDesc')}
        </p>
      </div>

      {/* Identity + logo */}
      <section className="space-y-4">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">{t('settingsForm.companyIdentity')}</h3>
        <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
          <div className="flex flex-col items-center gap-2">
            <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-xl border-2 border-zinc-300 bg-zinc-100 shadow-sm">
              {form.logoUrl ? (
                // eslint-disable-next-line jsx-a11y/img-redundant-alt
                <img src={mediaSrc(form.logoUrl)} alt={t('settingsForm.logo')} className="h-full w-full object-cover object-center" />
              ) : (
                <Building2 className="h-9 w-9 text-zinc-300" />
              )}
            </div>
            <ImageSourceButtons
              onFiles={(f) => void handleLogo(f)}
              disabled={uploading}
              size="sm"
              uploadLabel={form.logoUrl ? t('common.edit') : t('common.add')}
            />
            {uploading ? (
              <p className="inline-flex items-center gap-1 text-[11px] text-zinc-500">
                <Loader2 className="h-3 w-3 animate-spin" />
                {t('common.loading')}
              </p>
            ) : null}
            {uploadNote ? <p className="text-[11px] text-zinc-500">{uploadNote}</p> : null}
            {form.logoUrl && (
              <button
                type="button"
                onClick={() => {
                  logoTouched.current = true;
                  profileEpoch.current += 1;
                  set('logoUrl', '');
                }}
                className="text-[11px] font-semibold text-rose-600 hover:text-rose-700"
              >
                {t('common.delete')}
              </button>
            )}
          </div>

          <div className="grid flex-1 grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="app-label">{t('settingsForm.legalName')}</label>
              <input
                className="app-field"
                value={form.legalName}
                onChange={(e) => set('legalName', e.target.value)}
                placeholder="e.g. Edge Finances Pvt Ltd"
              />
            </div>
            <div>
              <label className="app-label">{t('settingsForm.displayName')}</label>
              <input
                className="app-field"
                value={form.displayName}
                onChange={(e) => set('displayName', e.target.value)}
                placeholder="e.g. Edge Finances"
              />
            </div>
            <div>
              <label className="app-label">{t('settingsForm.tagline')}</label>
              <input
                className="app-field"
                value={form.tagline}
                onChange={(e) => set('tagline', e.target.value)}
                placeholder="Finance & Accounting Management"
              />
            </div>
          </div>
        </div>
        {uploadNote && (
          <p className="inline-flex items-center gap-1.5 text-xs text-amber-700">
            <AlertTriangle className="h-3.5 w-3.5" />
            {uploadNote}
          </p>
        )}
      </section>

      {/* Contact */}
      <section className="space-y-4">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">{t('settingsForm.contact')}</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="app-label">{t('settingsForm.phone')}</label>
            <input
              type="tel"
              className="app-field"
              value={form.phone}
              onChange={(e) => set('phone', e.target.value)}
              placeholder="+91 90000 00000"
            />
          </div>
          <div>
            <label className="app-label">{t('settingsForm.altPhone')}</label>
            <input
              type="tel"
              className="app-field"
              value={form.altPhone}
              onChange={(e) => set('altPhone', e.target.value)}
            />
          </div>
          <div>
            <label className="app-label">{t('settingsForm.email')}</label>
            <input
              type="email"
              className="app-field"
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
              placeholder="contact@company.com"
            />
          </div>
          <div>
            <label className="app-label">{t('settingsForm.website')}</label>
            <input
              className="app-field"
              value={form.website}
              onChange={(e) => set('website', e.target.value)}
              placeholder="www.company.com"
            />
          </div>
        </div>
      </section>

      {/* Address */}
      <section className="space-y-4">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">{t('settingsForm.address')}</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="app-label">{t('settingsForm.addressLine1')}</label>
            <input
              className="app-field"
              value={form.addressLine1}
              onChange={(e) => set('addressLine1', e.target.value)}
              placeholder="Building, street"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="app-label">{t('settingsForm.addressLine2')}</label>
            <input
              className="app-field"
              value={form.addressLine2}
              onChange={(e) => set('addressLine2', e.target.value)}
              placeholder="Area, landmark (optional)"
            />
          </div>
          <div>
            <label className="app-label">{t('settingsForm.city')}</label>
            <input className="app-field" value={form.city} onChange={(e) => set('city', e.target.value)} />
          </div>
          <div>
            <label className="app-label">{t('settingsForm.state')}</label>
            <input className="app-field" value={form.state} onChange={(e) => set('state', e.target.value)} />
          </div>
          <div>
            <label className="app-label">{t('settingsForm.pincode')}</label>
            <input className="app-field" value={form.pincode} onChange={(e) => set('pincode', e.target.value)} />
          </div>
          <div>
            <label className="app-label">{t('settingsForm.country')}</label>
            <input className="app-field" value={form.country} onChange={(e) => set('country', e.target.value)} />
          </div>
        </div>
      </section>

      {/* Compliance */}
      <section className="space-y-4">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">{t('settingsForm.registration')}</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="app-label">{t('settingsForm.gstin')}</label>
            <input className="app-field" value={form.gstin} onChange={(e) => set('gstin', e.target.value)} />
          </div>
          <div>
            <label className="app-label">{t('settingsForm.registration')}</label>
            <input
              className="app-field"
              value={form.registrationNo}
              onChange={(e) => set('registrationNo', e.target.value)}
            />
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">{t('settingsForm.partners')}</h3>
          <button type="button" onClick={addPartner} className="app-btn-secondary inline-flex items-center gap-1.5 text-xs">
            <Plus className="h-3.5 w-3.5" />
            {t('settingsForm.addOwner')}
          </button>
        </div>
        {form.partners.length === 0 ? (
          <p className="rounded-md border border-dashed border-zinc-200 py-6 text-center text-sm text-zinc-500">
            {t('settingsForm.partners')}
          </p>
        ) : (
          <div className="space-y-3">
            {form.partners.map((p) => (
              <div key={p.id} className="rounded-md border border-zinc-200 bg-zinc-50/50 p-3">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-12">
                  <div className="sm:col-span-3">
                    <label className="app-label">{t('common.name')}</label>
                    <input
                      className="app-field"
                      value={p.name}
                      onChange={(e) => setPartner(p.id!, { name: e.target.value })}
                    />
                  </div>
                  <div className="sm:col-span-3">
                    <label className="app-label">{t('settingsForm.role')}</label>
                    <input
                      className="app-field"
                      value={p.role}
                      onChange={(e) => setPartner(p.id!, { role: e.target.value })}
                      placeholder="Director, Partner…"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="app-label">{t('settingsForm.phone')}</label>
                    <input
                      className="app-field"
                      value={p.phone}
                      onChange={(e) => setPartner(p.id!, { phone: e.target.value })}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="app-label">{t('settingsForm.shareholding')}</label>
                    <input
                      className="app-field"
                      value={p.share}
                      onChange={(e) => setPartner(p.id!, { share: e.target.value })}
                    />
                  </div>
                  <div className="flex items-end sm:col-span-2">
                    <button
                      type="button"
                      onClick={() => removePartner(p.id!)}
                      className="inline-flex items-center gap-1.5 rounded-md px-2 py-2 text-xs font-semibold text-rose-700 hover:bg-rose-50"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      {t('common.delete')}
                    </button>
                  </div>
                  <div className="sm:col-span-6">
                    <label className="app-label">{t('settingsForm.email')}</label>
                    <input
                      type="email"
                      className="app-field"
                      value={p.email}
                      onChange={(e) => setPartner(p.id!, { email: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {error && (
        <p className="inline-flex items-center gap-1.5 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
          <AlertTriangle className="h-4 w-4" />
          {error}
        </p>
      )}

      <div className="flex items-center justify-end gap-2 border-t border-zinc-100 pt-5">
        {saveState === 'saved' && (
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700">
            <CheckCircle2 className="h-4 w-4" />
            {t('common.saved')}
          </span>
        )}
        <button type="button" onClick={reset} className="app-btn-secondary">
          {t('common.cancel')}
        </button>
        <button type="submit" disabled={saveState === 'saving'} className="app-btn-primary inline-flex items-center gap-2">
          {saveState === 'saving' && <Loader2 className="h-4 w-4 animate-spin" />}
          {saveState === 'saving' ? t('common.loading') : t('settingsForm.saveCompany')}
        </button>
      </div>
    </form>
  );
};

export default CompanySettings;
